/**
 * For a string in yyyy-mm-dd format or yyyy-mm-ddThh:mm:ss.msZ format
 * , return a readable date: "Aug 21, 2020"
 * An optional field is available, to return a different datestyle:
 * "full", "long", "medium", "short"
 * @param dateString A string in the format yyyy-mm-dd
 * @returns A readable date string, e.g.: Aug 21, 2020
 */
export function getFormattedDate(
  dateString?: string | null,
  dateStyle: "full" | "long" | "medium" | "short" = "medium",
): string {
  if (!dateString) {
    return "–";
  }
  // regex for utc date string
  if (dateString.match(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}Z$/)) {
    dateString = dateString.split("T")[0];
  }

  if (dateString.search(/\d{4}-\d{2}-\d{2}/) === -1) {
    return "";
  }
  try {
    // parse date string
    // If the date is in ISO format, convert it first
    if (dateString.length > 10) {
      dateString = dateToString(dateString);
    }
    const sub = dateString.split("-").map(Number);
    const d = new Date(sub[0], sub[1] - 1, sub[2]);
    return new Intl.DateTimeFormat("en-US", {
      dateStyle,
    }).format(d);
  } catch (err) {
    return "";
  }
}

/**
 * Returns a date generated from the dateString given in
 * the query. String returned is in mm/dd/yy, hh:mm:ss (PM/AM) format. Can
 * be used when you have the full datetime string in YYYY-MM-DDTHH:MM:SS.MSZ format
 * or similar format.
 * @param dateString
 * @returns
 */
export function getFormattedDateAndTime(dateString: string): string {
  try {
    const d = new Date(dateString);
    return new Intl.DateTimeFormat("en-US", {
      dateStyle: "short",
      timeStyle: "medium",
    }).format(d);
  } catch (err) {
    return "";
  }
}

/**
 * Returns a date generated from the yyyy-mm-dd string input.
 * Make sure your input is in the yyyy-mm-dd format!
 * We need this because date strings coming out of the date input
 * are in the format yyyy-mm-dd, and when you make a new Date
 * from this format, it's incorrect. The date needs to be in the
 * format mm-dd-yyyy.
 * @param dateString: A string in the format yyyy-mm-dd
 * @returns A new Date from the string param
 */
export function getCalendarDate(dateString: string | undefined): Date {
  // for the Calendar component to use, it needs to be mm-dd-yyyy.
  if (!dateString) {
    return new Date();
  }
  if (dateString.search(/\d{4}-\d{2}-\d{2}/) === -1) {
    throw new Error("Date must be in format yyyy-mm-dd");
  }
  return new Date(`${dateString}T00:00:00`);
}

/**
 * Converts a Date to the format needed when saving the date: yyyy-mm-dd
 * `sv` is Sweden, where they use dates in the proper format (yyyy-mm-dd)
 * @param d A Date object to convert
 * @returns A string in the format yyyy-mm-dd
 */
export function dateToString(d: Date | string): string {
  if (typeof d === "string") {
    return d.substring(0, 10);
  }
  return d.toLocaleDateString("sv", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });
}

export function isYearMonth(
  dateString?: string | null,
  exact?: boolean,
): boolean {
  if (!dateString) {
    return false;
  }
  if (exact) {
    return /^\d{4}-\d{2}$/.test(dateString);
  } else {
    return /\d{4}-\d{2}/.test(dateString);
  }
}

export function isYearMonthDay(dateString?: string | null): boolean {
  if (!dateString) {
    return false;
  }
  return /^\d{4}-\d{2}-\d{2}$/.test(dateString);
}

/**
 * Converts a yearMonth (YYYY-MM) string to a formatted string
 * @param yearMonth The YYYY-MM to convert
 * @returns A string in the format "Feb 2020"
 */
export function yearMonthToDate(yearMonth: string): string {
  if (!isYearMonth(yearMonth)) {
    return yearMonth;
  }
  const sub = yearMonth.split("-").map(Number);
  const d = new Date(sub[0], sub[1] - 1);
  return new Intl.DateTimeFormat("en-US", {
    year: "numeric",
    month: "short",
  }).format(d);
}

export function getLastDayOfMonth(date: Date): Date {
  const year = date.getFullYear();
  const month = date.getMonth();

  return new Date(year, month + 1, 0);
}

export function getFirstDayOfMonth(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), 1);
}

export function getMonthsBetweenTwoDatesInclusive(
  startDate: Date,
  endDate: Date,
): number {
  if (startDate > endDate) return 0;

  const yearDifference = endDate.getFullYear() - startDate.getFullYear() - 1;
  const startDateMonthsToEndOfYear = 12 - startDate.getMonth(); //e.g. month = Oct -> 3
  const endDateMonthsFromBeginningOfYear = endDate.getMonth() + 1; //e.g. month = Oct -> 10

  return (
    yearDifference * 12 +
    startDateMonthsToEndOfYear +
    endDateMonthsFromBeginningOfYear
  );
}

export function getDaysMonthsOrWeeksSince(date1: Date, date2: Date) {
  const diff = date2.getTime() - date1.getTime();
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  const months = Math.floor(days / 30);
  const weeks = Math.floor(days / 7);

  if (days < 7) {
    if (days === 0) {
      return "Today";
    }
    return `${days} day${days === 1 ? "" : "s"} ago`;
  } else if (days < 30) {
    return `${weeks} week${weeks === 1 ? "" : "s"} ago`;
  } else {
    return `${months} month${months === 1 ? "" : "s"} ago`;
  }
}

export function getShortMonth(dateString: string | null | undefined) {
  if (!dateString || !isYearMonth(dateString, false)) return "";
  const [y, m] = dateString.split("-");
  const date = new Date(Number(y), Number(m) - 1);
  return new Date(date).toLocaleString("default", {
    month: "2-digit",
  });
}

export function isDateInFuture(dateString: string) {
  if (dateString.match(/\d{4}-\d{2}/)) {
    return isDateInFutureFourDigit(dateString);
  }

  const now = new Date();
  const month = parseInt(dateString.split("/")[0], 10);
  const year = 2000 + parseInt(dateString.split("/")[1], 10);

  if (year > now.getFullYear()) {
    return true;
  }

  if (year === now.getFullYear() && month > now.getMonth() + 1) {
    return true;
  }

  return false;
}

function isDateInFutureFourDigit(dateString: string) {
  const now = new Date();
  const year = parseInt(dateString.split("-")[0], 10);
  const month = parseInt(dateString.split("-")[1], 10);
  const day: number = parseInt(dateString.split("-")[2], 10);

  if (year > now.getFullYear()) {
    return true;
  }

  if (year === now.getFullYear() && month > now.getMonth() + 1) {
    return true;
  }

  if (
    !isNaN(day) &&
    year === now.getFullYear() &&
    month === now.getMonth() + 1 &&
    day > now.getDate()
  ) {
    return true;
  }

  return false;
}
